import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Send, Loader2 } from "lucide-react";
import { CONTACTS } from "@/lib/brand";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteContact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success(t.quote.successTitle, {
        description: t.quote.successDesc,
      });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(t.quote.errorTitle, {
        description: t.quote.errorDesc,
      });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-black/20 py-3.5 text-neutral-900 placeholder:text-neutral-400 focus:border-[#1478FF] focus:outline-none transition-colors duration-300";

  return (
    <section
      id="quote"
      className="relative py-24 md:py-32 bg-white border-t border-black/10"
      data-testid="quote-section"
    >
      <div className="pointer-events-none absolute top-10 left-1/4 h-[400px] w-[400px] blue-glow opacity-50" />
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-5 gap-14 lg:gap-20">
        {/* Left info */}
        <div className="lg:col-span-2">
          <Reveal>
            <p className="text-[#1478FF] font-semibold tracking-[0.28em] uppercase text-xs mb-5">
              {t.quote.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-semibold text-neutral-900 text-4xl md:text-6xl tracking-tight leading-[0.95]">
              {t.quote.titleL1}
              <br />
              {t.quote.titleL2}
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-6 text-neutral-600 max-w-sm leading-relaxed">
              {t.quote.sub}
            </p>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="mt-10 space-y-4">
              {CONTACTS.map((c) => (
                <a
                  key={c.name}
                  href={`tel:${c.phone}`}
                  className="group flex items-center gap-4"
                  data-testid={`contact-${c.roleKey}`}
                >
                  <span className="h-12 w-12 shrink-0 bg-[#1478FF] text-white flex items-center justify-center transition-transform group-hover:scale-105">
                    <Phone size={20} />
                  </span>
                  <span>
                    <span className="block text-neutral-900 font-semibold">
                      {c.name}
                    </span>
                    <span className="block text-xs text-neutral-500">
                      {t.roles[c.roleKey]} · {c.phone}
                    </span>
                  </span>
                </a>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Form */}
        <Reveal delay={0.1} className="lg:col-span-3">
          <form
            onSubmit={submit}
            className="bg-neutral-50 border border-black/10 p-8 md:p-10 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.15)]"
            data-testid="quote-form"
          >
            <div className="space-y-7">
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider">
                  {t.quote.nameLabel}
                </label>
                <input
                  required
                  placeholder={t.quote.namePh}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls}
                  data-testid="quote-input-name"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider">
                  {t.quote.emailLabel}
                </label>
                <input
                  required
                  type="email"
                  placeholder={t.quote.emailPh}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                  data-testid="quote-input-email"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider">
                  {t.quote.messageLabel}
                </label>
                <textarea
                  rows={4}
                  placeholder={t.quote.messagePh}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className={`${inputCls} resize-none`}
                  data-testid="quote-input-message"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-9 w-full inline-flex items-center justify-center gap-2 bg-[#1478FF] hover:bg-[#3B8BFF] active:scale-[0.98] text-white font-semibold py-4 rounded-full transition-all duration-300 hover:shadow-[0_0_30px_-4px_rgba(20,120,255,0.7)] disabled:opacity-60"
              data-testid="quote-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> {t.quote.sending}
                </>
              ) : (
                <>
                  {t.quote.submit} <Send size={17} />
                </>
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
